import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  CardContent as CardBody,
  CardDescription
} from '@shadcn/components/ui/card';
import { Col } from '@shadcn/components/ui/col';
import { Row } from '@shadcn/components/ui/row';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { backendUrl } from '@helpers/consts';

import Breadcrumb from '../../../components/Common/Breadcrumb';
import superagent from 'superagent';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const NestedOrderRate = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const formatRate = value => Number(value || 0).toFixed(2);

  useEffect(() => {
    fetchData();
  }, []);

  const handleDateFilter = () => {
    fetchData();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await superagent
        .post(`${backendUrl}/orders/nestedOrderRate`)
        .send({ startDate, endDate })
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('Accept', 'application/json');

      setData(response.body);
      setError(null);
    } catch (err) {
      setError(err.message || 'Cannot load nested order rate data');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const barChartData = useMemo(() => {
    if (!data?.byCountry)
      return {
        labels: [],
        datasets: []
      };

    const topCountries = [...data.byCountry]
      .sort((a, b) => b.total - a.total)
      .slice(0, 15);

    return {
      labels: topCountries.map(item => item.country),
      datasets: [
        {
          label: 'Nested Order Rate (%)',
          data: topCountries.map(item => item.nestedRate),
          backgroundColor: '#556ee6',
          borderColor: '#556ee6',
          borderWidth: 1,
          hoverBackgroundColor: '#4458b8',
          hoverBorderColor: '#4458b8'
        }
      ]
    };
  }, [data]);

  const renderHeader = () => (
    <div className="mb-6">
      <Row className="flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <Col className="w-full lg:w-1/2">
          <h2 className="text-2xl font-bold text-gray-800">
            Nested Order Rate Report
          </h2>
          <CardDescription className="mt-2">
            Analysis of nested vs non-nested orders across different countries
          </CardDescription>
        </Col>
        <Col className="w-full lg:w-1/2">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="w-full sm:w-auto flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="form-control w-full"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-auto flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                className="form-control w-full"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-auto flex items-end">
              <button
                className="btn btn-primary w-full sm:w-auto mt-5"
                onClick={handleDateFilter}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Loading nested order rate data...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-5">
          <h4>Error loading data</h4>
          <p className="text-danger">{error}</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="text-center p-5">
          <h4>No data available</h4>
          <p>Unable to load nested order rate data. Please try again later.</p>
        </div>
      );
    }

    return (
      <>
        <div className="mb-4">
          <Card>
            <CardBody className="py-4 px-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Card className="mini-stats-wid bg-light h-full">
                    <CardBody className="h-full p-4">
                      <div className="flex h-full">
                        <div className="flex-grow-1">
                          <p className="text-gray-700 fw-semibold text-sm mb-1">
                            Total Orders
                          </p>
                          <h4 className="mb-0 text-lg font-semibold">
                            {data.summary.total}
                          </h4>
                        </div>
                        <div className="avatar-xs rounded-circle bg-primary align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-primary">
                            <i className="bx bx-shopping-bag font-size-20"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
                <div>
                  <Card className="mini-stats-wid bg-light h-full">
                    <CardBody className="h-full p-4">
                      <div className="flex h-full">
                        <div className="flex-grow-1">
                          <p className="text-gray-700 fw-semibold text-sm mb-1">
                            Nested Orders
                          </p>
                          <h4 className="mb-0 text-lg font-semibold">
                            {data.summary.nested}
                          </h4>
                          <p className="text-info text-sm mt-1 mb-0">
                            {formatRate(data.summary.nestedRate)}%
                          </p>
                        </div>
                        <div className="avatar-xs rounded-circle bg-info align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-info">
                            <i className="bx bx-layer font-size-20"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
                <div>
                  <Card className="mini-stats-wid bg-light h-full">
                    <CardBody className="h-full p-4">
                      <div className="flex h-full">
                        <div className="flex-grow-1">
                          <p className="text-gray-700 fw-semibold text-sm mb-1">
                            Non-Nested Orders
                          </p>
                          <h4 className="mb-0 text-lg font-semibold">
                            {data.summary.nonNested}
                          </h4>
                          <p className="text-success text-sm mt-1 mb-0">
                            {formatRate(100 - data.summary.nestedRate)}%
                          </p>
                        </div>
                        <div className="avatar-xs rounded-circle bg-success align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-success">
                            <i className="bx bx-purchase-tag font-size-20"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-4">
          <div className="lg:col-span-12">
            <Card>
              <CardBody>
                <h4 className="card-title my-4">
                  Nested Order Rate by Country (Top 15)
                </h4>
                <div className="h-[400px]">
                  <Bar
                    data={barChartData}
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Rate (%)'
                          },
                          min: 0,
                          max: 100
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Country'
                          }
                        }
                      }
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        <div className="grid grid-cols-1 mb-4">
          <div>
            <Card>
              <CardBody>
                <h4 className="card-title my-4">Detailed Country Analysis</h4>
                <div className="overflow-x-auto">
                  <table className="table table-centered table-nowrap mb-0 min-w-full">
                    <thead className="table-light">
                      <tr>
                        <th className="py-3">Country</th>
                        <th className="py-3">Total Orders</th>
                        <th className="py-3">Nested Orders</th>
                        <th className="py-3">Non-Nested Orders</th>
                        <th className="py-3">Nested Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byCountry
                        .sort((a, b) => b.total - a.total)
                        .map(country => (
                          <tr key={country.country}>
                            <td className="py-3 font-medium">
                              {country.country}
                            </td>
                            <td className="py-3">{country.total}</td>
                            <td className="py-3">
                              <span className="text-info">
                                {country.nested}
                              </span>
                            </td>
                            <td className="py-3">
                              <span className="text-success">
                                {country.nonNested}
                              </span>
                            </td>
                            <td className="py-3">
                              <span
                                className={
                                  country.nestedRate > 20
                                    ? 'text-warning'
                                    : 'text-info'
                                }
                              >
                                {formatRate(country.nestedRate)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </>
    );
  };

  return (
    <React.Fragment>
      <div className="px-2 sm:px-4">
        {renderHeader()}
        {renderContent()}
      </div>
    </React.Fragment>
  );
};

export default NestedOrderRate;
